import type { ServicePluginContext } from "@brains/plugins";
import type { SchedulerMessagePublisher } from "../types/scheduler";
import type { Logger } from "@brains/utils/logger";
import type { QueueManager } from "../queue-manager";
import type { ProviderRegistry } from "../provider-registry";
import type { RetryTracker } from "../retry-tracker";
import { ContentScheduler } from "../scheduler";
import type { PublishEntityExecutor } from "../publish-executor";
import { BunSchedulerBackend } from "../scheduler-backend";
import type { ContentPipelineConfig } from "../types/config";
import { checkGenerationConditions } from "./generation-conditions";

export interface CreateSchedulerDeps {
  context: ServicePluginContext;
  config: ContentPipelineConfig;
  queueManager: QueueManager;
  providerRegistry: ProviderRegistry;
  retryTracker: RetryTracker;
  publishExecutor: PublishEntityExecutor;
  logger: Logger;
}

/**
 * Create and configure a ContentScheduler instance with message bus integration.
 */
export function createScheduler(deps: CreateSchedulerDeps): ContentScheduler {
  const {
    context,
    config,
    queueManager,
    providerRegistry,
    retryTracker,
    publishExecutor,
    logger,
  } = deps;

  const messageBus = createMessageBusAdapter(context);

  return ContentScheduler.createFresh({
    queueManager,
    providerRegistry,
    retryTracker,
    logger,
    backend: new BunSchedulerBackend({
      onOverlapSkipped: (jobKey): void => {
        logger.debug("Skipping overlapping content scheduler cycle", {
          jobKey,
        });
      },
      onCallbackError: (jobKey, error): void => {
        logger.error("Unhandled content scheduler callback error", {
          jobKey,
          error,
        });
      },
    }),
    ...(config.entitySchedules && {
      entitySchedules: config.entitySchedules,
    }),
    ...(config.generationSchedules && {
      generationSchedules: config.generationSchedules,
    }),
    ...(config.generationConditions && {
      generationConditions: config.generationConditions,
    }),
    messageBus,
    publishExecutor,
    onCheckGenerationConditions: (entityType, conditions) =>
      checkGenerationConditions(
        context.entityService,
        logger,
        entityType,
        conditions,
      ),
  });
}

/**
 * The scheduler publishes events and reads no responses, so the adapter returns
 * the publisher it needs rather than a whole message bus whose other members
 * it would have to stub as no-ops.
 */
function createMessageBusAdapter(
  context: ServicePluginContext,
): SchedulerMessagePublisher {
  const send: SchedulerMessagePublisher["send"] = async (request) => {
    return context.messaging.send({
      type: request.type,
      payload: request.payload,
      ...(request.broadcast !== undefined
        ? { broadcast: request.broadcast }
        : {}),
    });
  };

  return { send };
}
